
/* eslint-disable max-lines-per-function */
import React, { useEffect } from "react";
import { css } from "@emotion/css";
import Box from "@mui/system/Box";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import styled from "@emotion/styled";
import Header from "./Header";
import Footer from "./Footer";
import en from '../../nls/en.json';
import { Autocomplete, Button, TextField } from "@mui/material";
import { useDispatch } from "react-redux";
import { getDistrictList, getSchoolAdminRoles, putSchoolAdmin } from "../../store/userSlice";
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getUserInfo, putSurvey, setLoggedInUserId } from "../../store/studentSlice";
import { getUserId, isEmpty } from "../../utils/constants";

const gradientBg =
  "linear-gradient(320deg, rgba(21,105,163,1) 0%, rgba(23,107,166,1) 43%, rgba(68,164,235,1) 99%)";
const theme = createTheme({
  typography: {
    fontFamily: ["Poppins, sans-serif"].join(","),
  },
  palette: {
    primary: {
      main: "#1468A2",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        // Name of the slot
        root: {
          fontSize: "1rem",
          fontWeight: "500",
          padding: '9.2px 15px',
          textTransform: "none"
        },
      },
      variants: [
        {
          props: { variant: "outlined" },
          style: {
            color: "#333",
            border: "1px solid #666",
            "&:hover": {
              backgroundColor: "#1468A2",
              color: "#fff",
            },
          },
        },
      ],
    },
  },
});

theme.typography.h1 = {
  fontSize: 30,
  fontWeight: 600,
  color: "#1468A2",
  textAlign: "center",
};
theme.typography.h5 = {
  fontSize: 24,
  fontWeight: 600,
  color: "#666",
  textAlign: "center",
};

theme.typography.body2 = {
  fontSize: 13,
  fontWeight: 400,
  color: "#666",
  textAlign: "center",
};

const LoginBox = styled.div`
  background-color:#fff;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
//   height: 375px;
  width: 400px;
  @media (max-width: 767px) {
    height:auto;
    width: 100%;
  }
`;

const schema = yup.object().shape({
  name: yup.string().required('name is required'),
  district: yup.object().shape({
    id: yup.string(),
    label: yup.string()
  }).required('District is required'),
  role: yup.object().shape({
    id: yup.string(),
    label: yup.string()
  }).required('Role is required'),
  other_role: yup.string().nullable(),
});

const defaultValues = {
  name: "",
  email:"",
  role: {},
  district: null,
  other_role: ""
}

const SchoolAdminBasicInfo = () => {
  const { loggedInUserId, userInfo } = useSelector(({ studentInfoSlice }) => studentInfoSlice );
  const { rolesOfSchoolAdmin, districtList } = useSelector(({ userSliceHome }) => userSliceHome );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchQueryDistrict, setSearchQueryDistrict] = React.useState('');
  const [isOtherRole, setIsOtherRole] = React.useState(false);
  const { handleSubmit, control, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    shouldUnregister: false,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const handleDistrictInputChange = (event, newInputValue) => {
    setSearchQueryDistrict(newInputValue);
    setTimeout(() => {
      dispatch(getDistrictList(1, [], newInputValue));
    }, 800);
  };
  const loadMoreResultsForMentor = () => {
    if (districtList?.pagination?.isNextPage) {
      dispatch(getDistrictList(districtList?.pagination?.nextPage, districtList?.result, searchQueryDistrict ))
    }
  }

  function onSucces() {
    navigate("/user/schoolAdmin");
    dispatch(putSurvey(loggedInUserId));
  }

  const onSubmit = (data) => {
    const {district, name, other_role, role } = data;
    const payload = {
          name,
          role: role.id,
          district: district.id,
          user_id: loggedInUserId
      }

    if (other_role) {
      payload.other_role = other_role;
    }

      if(isEmpty(errors)){
        dispatch(putSchoolAdmin(payload, onSucces))
      }
  }

  useEffect(() => {
    let userId = getUserId()
    dispatch(setLoggedInUserId(userId));
    dispatch(getSchoolAdminRoles());
    dispatch(getDistrictList(1,[],""));
    dispatch(getUserInfo());
  },[])

  return (
    <ThemeProvider theme={theme}>
      <Box
        component="section"
        className={css`
          background: ${gradientBg};
          display: flex;
          align-items: center;
          justify-content: center;
          align-content: space-between;
          flex-wrap: wrap;
          padding: 30px;
          width: 100%;
        `}
      >
        <Header />
        <LoginBox>
          <CardContent
            className={css`
              padding: 30px 30px 24px 30px !important;
              text-align: center;
            `}
          >
            <Typography variant="h1" component="h1">
              {en.BASIC_INFO_TITLE}
            </Typography>
            <Box
              component="form"
              autoComplete="off"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              sx={{ mt: 1, mb: 2 }}
            >
              <Controller
                name="name"
                control={control}
                render={({ field: { value, onChange, onBlur } }) => (
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="name"
                    label="Name"
                    onChange={onChange}
                    helperText={errors?.name?.message}
                    value={value}
                    error={!!errors?.name}
                    onBlur={onBlur}
                  />
                )}
              />
              <TextField
                margin="normal"
                fullWidth
                id="email"
                label="Email"
                value={userInfo?.email}
                disabled={true}
                InputProps={{
                  readOnly: true
                }}
              />

              <Controller
                name="district"
                control={control}
                render={({  field: { value, onChange, ...rest } }) => (
                  <Autocomplete
                    {...rest}
                    options={districtList?.result || []}
                    getOptionLabel={(option) => option.name || ''}
                    inputValue={searchQueryDistrict}
                    value={value}
                    onChange={(event, newValue) => {
                      onChange(newValue);
                    }}
                    onInputChange={handleDistrictInputChange}
                    ListboxProps={{
                    onScroll: (event) => {
                       const listboxNode = event.currentTarget;
                       if(Math.ceil(listboxNode.scrollTop) + listboxNode.clientHeight === listboxNode.scrollHeight) {
                       loadMoreResultsForMentor()
                     }
                   }
                }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="District"
                        variant="outlined"
                        sx={{ mt: 2 }}
                        error={!!errors.district}
                        helperText={errors.district?.message}
                      />
                    )}
                  />
                )}
              />
              <Controller
                name="role"
                control={control}
                render={({ field: { value, onChange, ...rest } }) => (
                  <Autocomplete
                    {...rest}
                    options={rolesOfSchoolAdmin?.result || []}
                    getOptionLabel={(option) => option.name || ''}
                    value= {value}
                    onChange={(event, newValue) => {
                      onChange(newValue);
                      setIsOtherRole(newValue?.name === "OTHER");
                    }}
                    renderInput={(params) => (
                      <>
                        <TextField
                          {...params}
                          label="Title / Role"
                          variant="outlined"
                          sx={{ mt: 2 }}
                          required
                          error={!!errors.role}
                          helperText={errors.role?.message}
                        />
                      </>
                    )} />
                )}
              />
             {
              isOtherRole && 
              <Controller
                name="other_role"
                control={control}
                render={({ field: { value, onChange, onBlur } }) => (
                  <TextField
                    margin="normal"
                    fullWidth
                    id="other_role"
                    label="Other role"
                    onChange={onChange}
                    helperText={errors?.other_role?.message}
                    value={value}
                    error={!!errors?.other_role}
                    onBlur={onBlur}
                  />
                )}
              />
             }
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                {en.SAVE}
              </Button>
            </Box>
          </CardContent>
        </LoginBox>
        <Footer isFromSchoolAdminBasicInfo={true} />
      </Box>
    </ThemeProvider>
  );
};

export default SchoolAdminBasicInfo;